/**
 * Weekly Timesheet Component
 * L0-CMD-2026-0120-012 - Hours Logging System
 *
 * Displays a weekly view of time entries with navigation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { TimeEntryForm } from './TimeEntryForm';

interface TimeEntry {
  id: number;
  project_id: number;
  project_name: string;
  project_number: string;
  date: string;
  hours: number;
  task_type: string;
  description: string;
  is_billable: boolean;
  status: string;
}

interface Project {
  id: number;
  name: string;
  number: string;
}

interface WeeklyTimesheetProps {
  compact?: boolean;
}

export function WeeklyTimesheet({ compact = false }: WeeklyTimesheetProps) {
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.getFullYear(), today.getMonth(), diff);
  });
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getWeekDays = useCallback(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      return day;
    });
  }, [weekStart]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 6);

    try {
      const response = await fetch(
        `/api/time/entries?start_date=${weekStart.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setEntries(data.entries || []);
      } else {
        setError(data.error || 'Failed to fetch entries');
      }
    } catch (err) {
      setError('Failed to fetch time entries');
      console.error('Failed to fetch entries:', err);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch('/api/assignments/projects/assigned', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchProjects();
  }, [fetchEntries, fetchProjects]);

  const handleLogTime = async (entry: any) => {
    const response = await fetch('/api/time/entries', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(entry),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to log time');
    }

    fetchEntries();
    setShowForm(false);
  };

  const deleteEntry = async (entryId: number) => {
    if (!confirm('Delete this time entry?')) return;

    try {
      await fetch(`/api/time/entries/${entryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      fetchEntries();
    } catch (err) {
      alert('Failed to delete entry');
    }
  };

  const navigateWeek = (direction: number) => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + direction * 7);
    setWeekStart(newDate);
  };

  const weekDays = getWeekDays();
  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);
  const billableHours = entries.filter(e => e.is_billable).reduce((sum, e) => sum + e.hours, 0);

  // Group entries by project
  const entriesByProject = entries.reduce((acc, entry) => {
    if (!acc[entry.project_id]) {
      acc[entry.project_id] = {
        name: entry.project_name,
        number: entry.project_number,
        entries: [],
      };
    }
    acc[entry.project_id].entries.push(entry);
    return acc;
  }, {} as Record<number, { name: string; number: string; entries: TimeEntry[] }>);

  const formatShortDate = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="weekly-timesheet">
      <style>{`
        .weekly-timesheet {
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }
        .timesheet-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .week-nav {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .week-nav button {
          width: 32px;
          height: 32px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          color: #6b7280;
        }
        .week-nav button:hover {
          background: #f3f4f6;
        }
        .week-label {
          font-weight: 600;
          color: #111827;
          min-width: 200px;
          text-align: center;
        }
        .week-summary {
          display: flex;
          gap: 1rem;
          font-size: 0.875rem;
        }
        .summary-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: #6b7280;
        }
        .summary-item.billable {
          color: #059669;
        }
        .add-time-btn {
          padding: 0.5rem 1rem;
          background: #2563eb;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
        }
        .add-time-btn:hover {
          background: #1d4ed8;
        }
        .form-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-content h3 {
          margin: 0 0 1rem 0;
          font-size: 1.25rem;
          color: #111827;
        }
        .timesheet-grid {
          overflow-x: auto;
        }
        .grid-header, .grid-row {
          display: grid;
          grid-template-columns: 200px repeat(7, 1fr) 80px;
          min-width: 800px;
        }
        .grid-header {
          background: #f8fafc;
          border-bottom: 1px solid #e5e7eb;
          font-weight: 600;
          font-size: 0.75rem;
          color: #6b7280;
          text-transform: uppercase;
        }
        .col {
          padding: 0.75rem 0.5rem;
          text-align: center;
          border-right: 1px solid #e5e7eb;
        }
        .col:last-child {
          border-right: none;
        }
        .project-col {
          text-align: left;
        }
        .day-col {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .day-name {
          font-size: 0.7rem;
        }
        .day-date {
          font-size: 0.875rem;
          font-weight: 600;
          color: #111827;
        }
        .grid-row {
          border-bottom: 1px solid #e5e7eb;
        }
        .grid-row:hover {
          background: #f9fafb;
        }
        .hours-cell {
          font-weight: 500;
          color: #2563eb;
        }
        .empty-cell {
          color: #d1d5db;
        }
        .entry-actions {
          display: flex;
          gap: 2px;
        }
        .delete-entry-btn {
          border: none;
          background: transparent;
          color: #d1d5db;
          cursor: pointer;
          font-size: 0.875rem;
          line-height: 1;
          padding: 0 2px;
        }
        .delete-entry-btn:hover {
          color: #dc2626;
        }
        .total-col {
          font-weight: 600;
          color: #111827;
        }
        .empty-state {
          padding: 3rem;
          text-align: center;
          color: #6b7280;
        }
        .empty-state svg {
          margin-bottom: 1rem;
          color: #d1d5db;
        }
        .empty-state button {
          margin-top: 1rem;
          padding: 0.5rem 1rem;
          background: #2563eb;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        .loading {
          padding: 2rem;
          text-align: center;
          color: #6b7280;
        }
        .error {
          padding: 1rem;
          background: #fef2f2;
          color: #dc2626;
          text-align: center;
        }
      `}</style>

      {/* Header */}
      <div className="timesheet-header">
        <div className="week-nav">
          <button onClick={() => navigateWeek(-1)} title="Previous week">
            ‹
          </button>
          <span className="week-label">
            {formatShortDate(weekStart)} - {formatShortDate(weekDays[6])}
            , {weekDays[6].getFullYear()}
          </span>
          <button onClick={() => navigateWeek(1)} title="Next week">
            ›
          </button>
        </div>

        {!compact && (
          <div className="week-summary">
            <div className="summary-item">
              <span>⏱</span>
              <span>{totalHours.toFixed(1)} hrs total</span>
            </div>
            <div className="summary-item billable">
              <span>{billableHours.toFixed(1)} billable</span>
            </div>
          </div>
        )}

        <button className="add-time-btn" onClick={() => setShowForm(true)}>
          + Log Time
        </button>
      </div>

      {/* Time Entry Form Modal */}
      {showForm && (
        <div className="form-modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Log Time</h3>
            <TimeEntryForm
              projects={projects}
              onSubmit={handleLogTime}
              initialDate={new Date()}
              onCancel={() => setShowForm(false)}
            />
          </div>
        </div>
      )}

      {/* Error */}
      {error && <div className="error">{error}</div>}

      {/* Loading */}
      {loading && <div className="loading">Loading timesheet...</div>}

      {/* Timesheet Grid */}
      {!loading && !error && (
        <div className="timesheet-grid">
          <div className="grid-header">
            <div className="col project-col">Project</div>
            {weekDays.map(day => (
              <div key={day.toISOString()} className="col day-col">
                <span className="day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                <span className="day-date">{day.getDate()}</span>
              </div>
            ))}
            <div className="col total-col">Total</div>
          </div>

          {/* Rows by project */}
          {Object.entries(entriesByProject).map(([projectId, project]) => {
            const projectTotal = project.entries.reduce((sum, e) => sum + e.hours, 0);

            return (
              <div key={projectId} className="grid-row">
                <div className="col project-col">
                  <div style={{ fontWeight: 500 }}>{project.number}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{project.name}</div>
                </div>
                {weekDays.map(day => {
                  const dayStr = day.toISOString().split('T')[0];
                  const dayEntries = project.entries.filter(e => e.date === dayStr);
                  const dayTotal = dayEntries.reduce((sum, e) => sum + e.hours, 0);

                  return (
                    <div key={dayStr} className="col day-col">
                      {dayTotal > 0 ? (
                        <>
                          <span className="hours-cell">{dayTotal.toFixed(1)}</span>
                          <div className="entry-actions">
                            {dayEntries.map(e => (
                              <button
                                key={e.id}
                                className="delete-entry-btn"
                                onClick={() => deleteEntry(e.id)}
                                title={`Delete ${e.hours.toFixed(1)}h entry${e.description ? `: ${e.description}` : ''}`}
                              >
                                ×
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <span className="empty-cell">-</span>
                      )}
                    </div>
                  );
                })}
                <div className="col total-col">
                  {projectTotal.toFixed(1)}
                </div>
              </div>
            );
          })}

          {entries.length === 0 && (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <p>No time logged this week</p>
              <button onClick={() => setShowForm(true)}>Log your first entry</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WeeklyTimesheet;
